<template>
  <button
    :id="getId()"
    :class="computedClasses"
    :disabled="isDisabled"
    @click="onClick"
    :form="form"
  >
    <template v-if="isBusy">
      <HpIcon name="spinner" class="inline animate-spin text-gray" />
    </template>
    <template v-else>
      <span :class="spanClassesComputed">
        <HpIcon
          v-if="!!icon && iconLocation === 'left'"
          :name="icon"
          :class="iconClassesComputed"
        ></HpIcon>
        <slot />
        <HpIcon
          v-if="!!icon && iconLocation === 'right'"
          :name="icon"
          :class="iconClassesComputed"
        ></HpIcon>
      </span>
    </template>
  </button>
</template>

<script lang="ts" setup>
import type { PropType} from "@vue/runtime-core";
import { extractText } from '../../utils/analytics'
  import type { SlotItem } from '../../utils/analytics'

  export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'minimal'
    | 'none'
    | 'danger'
    | 'secondary-danger'
    | 'success'
  export type IconLocation = 'left' | 'right'

  const props = defineProps({
    ...Id.props,
    disabled: Boolean,
    busy: Boolean,
    variant: {
      type: String as PropType<ButtonVariant>,
      default: 'primary'
    },
    form: String,
    icon: String,
    spanClasses: String,
    iconClasses: String,
    iconLocation: {
      type: String as PropType<IconLocation>,
      default: 'left'
    },
    compact: Boolean,
    narrow: {
      type: Boolean,
      default: false
    },
    ...Analytics.props
  })

  const emit = defineEmits<{ (event: 'click', payload: any): void }>()

  const { id, disabled, busy } = toRefs(props)

  const { getId } = Id.use(id)

  const isDisabled = BooleanLike.use(disabled)
  const isBusy = BooleanLike.use(busy)

  const computedClasses = computed(() => [
    'btn',
    {
      'pointer-events-none': isBusy.value,
      'btn-compact': props.compact,
      'btn-narrow': props.narrow,
      'btn-primary': props.variant === 'primary',
      'btn-secondary': props.variant === 'secondary',
      'btn-minimal': props.variant === 'minimal',
      'btn-danger': props.variant === 'danger',
      'btn-secondary-danger': props.variant === 'secondary-danger',
      'btn-success': props.variant === 'success'
    }
  ])

  const iconClassesComputed = computed(() => [
    'btn-icon',
    props.iconClasses,
    props.iconLocation === 'left' ? 'mr-2' : 'ml-2'
  ])

  const spanClassesComputed = computed(() => ['btn-icon', props.spanClasses])

  const { buttonClick } = Analytics.use()

  const slots = useSlots()
  const onClick = (ev: Event) => {
    try {
      buttonClick({
        elementId: getId.value(),
        text:
          extractText((slots?.default?.() as Array<SlotItem>) ?? []) ||
          (props.icon ? `icon(${props.icon})` : ''),
        ...Analytics.getEventData(props.eventData)
      })
    } catch (ex) {
      console.error(ex)
    }

    emit('click', ev)
  }

  defineExpose({ onClick })
</script>

<style scoped lang="postcss"></style>
